"use client";

import { useState, useEffect, useRef } from "react";
import styles from "./styles.module.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogBody,
} from "../../ui/dialog";
import Button from "../../button-v1";
import { useQuery } from "@tanstack/react-query";
import { getProviders } from "../../../services/case-manager/case-manager";
import { getExternalProviders } from "../../../services/admin/providers";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { getPatientGeneralDetails } from "../../../services/patient/details";
import { CiFileOn, CiImageOn } from "react-icons/ci";
import AddExternalStaff from "../../staff/external/add";
import useRoles from "../../../hooks/useRoles";

// Display labels for the File type selector (SCRUM-192).
const FORMAT_LABELS = {
  png: "PNG",
  pdf: "PDF",
  excel: "Excel",
  csv: "CSV",
};

export default function ShareModal({
  isOpen,
  setIsOpen,
  onShare,
  isSharing = false,
  title = "Share",
  shareButtonText = "Share",
  cancelButtonText = "Cancel",
  showProviderToggle = true,
  showExternalProvider = true,
  showFormatSelector = true,
  // SCRUM-192: explicit opt-in for the live "File type" selector. Defaults to
  // false so existing consumers (careplan, billing, patient documents, demo
  // download) are byte-for-byte unchanged: the legacy `showFormatSelector`
  // block below has been commented out for a while, and several of those
  // callers still pass showFormatSelector={true} expecting nothing. Only the
  // OOR Report opts into the selector via this prop.
  showFileTypeSelector = false,
  availableFormats = ["pdf", "png"],
  defaultFormat = "pdf",
  defaultShareType = "provider",
  patientId = null,
  customProviders = null,
  customExternalProviders = null,
  customPatients = null,
  disabled = false,
  className = "",
}) {
  // Use provided patientId or get from sessionStorage
  const { roles } = useRoles();
  const currentPatientId = patientId || sessionStorage.getItem("_pt_id");

  const [provider, setProvider] = useState("");
  const [externalProvider, setExternalProvider] = useState("");
  const [shareType, setShareType] = useState(defaultShareType);
  const [providerName, setProviderName] = useState("");
  const [externalProviderName, setExternalProviderName] = useState("");
  const [format, setFormat] = useState(defaultFormat);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");

  // Add state for managing add external staff modal
  const [showAddExternalStaffModal, setShowAddExternalStaffModal] =
    useState(false);

  // Get patient data
  const { data: patient } = useQuery(
    ["patient", currentPatientId],
    () => getPatientGeneralDetails(currentPatientId),
    {
      enabled: !!currentPatientId,
    }
  );

  const isPatient = JSON.parse(localStorage.getItem("user"))?.account_roles?.[0]?.name === "Patient";


  // Get internal providers
  const {
    data: providers,
    isLoading: isLoadingProviders,
    error: providersError,
    refetch: refetchProviders,
  } = useQuery(["providers"], getProviders, {
    enabled: isOpen && !isPatient && !customProviders,
  });

  // Get external providers
  const {
    data: externalProviders,
    isLoading: isLoadingExternalProviders,
    error: externalProvidersError,
    refetch: refetchExternalProviders,
  } = useQuery(["external-providers"], () => getExternalProviders(true), {
    enabled: isOpen && showExternalProvider && !customExternalProviders,
  });

  // Use custom providers if provided, otherwise use fetched data
  const finalProviders = customProviders || providers || [];
  const finalExternalProviders =
    customExternalProviders || externalProviders || [];

  // Handle add external staff success - close modal and reopen share modal
  const handleAddExternalStaffSuccess = () => {
    setShowAddExternalStaffModal(false);
    // Refetch external providers to get the newly added one
    refetchExternalProviders();
    // Reopen the share modal
    setIsOpen(true);
  };

  // Handle closing add external staff modal - reopen share modal
  const handleCloseAddExternalStaff = () => {
    setShowAddExternalStaffModal(false);
    // Reopen the share modal
    setIsOpen(true);
  };

  // Handle opening add external staff modal - close share modal first
  const handleOpenAddExternalStaff = () => {
    setIsOpen(false); // Close share modal
    setShowAddExternalStaffModal(true); // Open add external staff modal
  };

  const handleProviderSelection = (type, value) => {
    if (type === "internal") {
      setProvider(value);
    } else {
      if (value) {
        setExternalProvider(value);
      } else {
        setExternalProvider("");
      }
    }
  };

  const handleFormatChange = (value) => {
    setFormat(value);
  };
  const handlePatientSelection = (value) => {
    setSelectedPatientId(value);
    const match = (customPatients || []).find(
      (p) => String(p.id) === String(value)
    );
    setSelectedPatientName(match?.name || "");
    setProvider(match?.email || "");
  };

  const handleShare = () => {
    const shareData = {
      format,
      // A report-patient pick (OOR) takes precedence over the ambient patient.
      patientId: selectedPatientId || currentPatientId,
      provider,
      externalProvider,
      shareType,
      providerName,
      externalProviderName,
    };

    onShare(shareData);
  };

  const handleCancel = () => {
    setIsOpen(false);
    // Reset form state
    setProvider("");
    setExternalProvider("");
    setShareType(defaultShareType);
    setProviderName("");
    setExternalProviderName("");
    setFormat(defaultFormat);
    setSelectedPatientId("");
    setSelectedPatientName("");
  };

  // Update provider name when provider changes
  useEffect(() => {
    const selectedProvider = finalProviders?.find(
      (_provider) => _provider.email === provider
    );

    if (selectedProvider) {
      setProviderName(
        selectedProvider.first_name + " " + selectedProvider.last_name
      );
    }
  }, [provider, finalProviders]);

  // Update external provider name when external provider changes
  useEffect(() => {
    if (!externalProvider) {
      setExternalProviderName("");
      return;
    }
    const selectedExternalProvider = finalExternalProviders?.find(
      (_provider) => _provider.email === externalProvider
    );

    if (selectedExternalProvider) {
      setExternalProviderName(selectedExternalProvider.name);
    }
  }, [externalProvider, finalExternalProviders]);

  // Handle patient share type
  const hasCustomPatients = !!customPatients;
  const prevShareTypeRef = useRef(shareType);
  useEffect(() => {
    const tabChanged = prevShareTypeRef.current !== shareType;
    prevShareTypeRef.current = shareType;
    if (hasCustomPatients) {
      if (tabChanged) {
        setProvider("");
        setProviderName("");
      }
      return;
    }
    if (shareType === "patient" && patient?.user) {
      setProvider(patient.user.email);
      setProviderName(patient.user.first_name + " " + patient.user.last_name);
    }
  }, [shareType, patient, hasCustomPatients]);

  const isFormValid = () => {
    if (shareType === "patient") {
      if (customPatients) return (!!selectedPatientId || !!provider) && !!format;
      return !!provider && !!format;
    }
    return !!provider || (!!externalProvider && !!format);
  };

  const isLoading = isLoadingProviders || isLoadingExternalProviders;

  return (
    <>
   
      <Dialog open={isOpen} onOpenChange={setIsOpen} setOpen={handleCancel}>
        <DialogContent className={className}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className={styles.container}>
              {showProviderToggle && (
                <section className={styles.action_container}>
                  <section className={styles.buttonGroup}>
                    <Button
                      variant={shareType === "provider" ? "primary" : "ghost"}
                      onClick={() => setShareType("provider")}
                      disabled={disabled}
                    >
                      Provider
                    </Button>
                    <Button
                      variant={shareType === "patient" ? "primary" : "ghost"}
                      onClick={() => setShareType("patient")}
                      disabled={disabled}
                    >
                      Patient
                    </Button>
                  </section>

                  {!roles.includes("Patient") && (
                    <section className={styles.buttonGroup}>
                      {shareType === "provider" && (
                        <Button onClick={handleOpenAddExternalStaff}>
                          + Add External Staff
                        </Button>
                      )}
                    </section>
                  )}
                </section>
              )}

              {shareType == "provider" && (
                <div className={styles.formGroup}>
                  <section className={styles.inputGroup}>
                    <label htmlFor="provider" className="label">
                      Provider
                    </label>
                    <Select
                      onChange={(value) =>
                        handleProviderSelection("internal", value)
                      }
                      value={provider}
                      disabled={shareType === "patient" || disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a provider">
                          <p>{providerName || "Select a provider"}</p>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem key="none" value="">
                          -- None --
                        </SelectItem>
                        {finalProviders?.map((provider) => (
                          <SelectItem key={provider.id} value={provider.email}>
                            {provider.first_name} {provider.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </section>

                  {showExternalProvider && (
                    <>
                      <section className={styles.inputGroup}>
                        <label htmlFor="externalProvider" className="label">
                          External Provider
                        </label>
                        <Select
                          onChange={(value) =>
                            handleProviderSelection("external", value)
                          }
                          value={externalProvider}
                          disabled={shareType === "patient" || disabled}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select an external provider">
                              <p>
                                {externalProviderName ||
                                  "Select an external provider"}
                              </p>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem key="none" value="">
                              -- None --
                            </SelectItem>
                            {finalExternalProviders?.map((provider) => (
                              <SelectItem
                                key={provider.id}
                                value={provider.email}
                              >
                                {provider.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </section>
                    </>
                  )}
                </div>
              )}

              {shareType === "patient" && customPatients && (
                <div className={styles.formGroup}>
                  <section className={styles.inputGroup}>
                    <label htmlFor="patient" className="label">
                      Patient
                    </label>
                    <Select
                      onChange={(value) => handlePatientSelection(value)}
                      value={selectedPatientId}
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a patient">
                          <p>{selectedPatientName || "Select a patient"}</p>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent searchField={true}>
                        <SelectItem key="none" value="">
                          -- None --
                        </SelectItem>
                        {customPatients.map((patient) => (
                          <SelectItem
                            key={patient.id}
                            value={String(patient.id)}
                          >
                            {patient.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </section>
                </div>
              )}

              <div className={styles.formGroup}>
                <section className={styles.inputGroup}>
                  {shareType === "patient" ? (
                    <label htmlFor="patientEmail" className="label">
                      Patient Email
                    </label>
                  ) : (
                    <label htmlFor="providerEmail" className="label">
                      Provider Email
                    </label>
                  )}

                  <input
                    id="providerEmail"
                    type="email"
                    className={styles.input}
                    placeholder="Enter email address"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                  // disabled={shareType === "patient" || disabled}
                  />
                </section>

                {showExternalProvider && shareType === "provider" && (
                  <section className={styles.inputGroup}>
                    <label htmlFor="externalProviderEmail" className="label">
                      External Provider Email
                    </label>
                    <input
                      id="externalProviderEmail"
                      type="email"
                      className={styles.input}
                      placeholder="Enter email address"
                      value={externalProvider}
                      onChange={(e) => setExternalProvider(e.target.value)}
                      disabled={shareType === "patient" || disabled}
                    />
                  </section>
                )}
              </div>
              {/* SCRUM-192: live "File type" selector, opt-in via
                  showFileTypeSelector (OOR Report). PNG / PDF / Excel / CSV
                  rendered from availableFormats. The legacy showFormatSelector
                  block was disabled; this preserves it for opted-in callers
                  only without touching the others. */}
              {showFileTypeSelector && (
                <div className={styles.formGroup}>
                  <section className={styles.inputGroup}>
                    <label htmlFor="format" className="label">
                      File type
                    </label>
                    <Select
                      onChange={(value) => handleFormatChange(value)}
                      value={format}
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="File type">
                          <p>{FORMAT_LABELS[format] || "File type"}</p>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {availableFormats.includes("png") && (
                          <SelectItem value="png" className={styles.selectItem}>
                            <CiImageOn size={18} />
                            PNG
                          </SelectItem>
                        )}
                        {availableFormats.includes("pdf") && (
                          <SelectItem value="pdf" className={styles.selectItem}>
                            <CiFileOn size={18} />
                            PDF
                          </SelectItem>
                        )}
                        {availableFormats.includes("excel") && (
                          <SelectItem
                            value="excel"
                            className={styles.selectItem}
                          >
                            <CiFileOn size={18} />
                            Excel
                          </SelectItem>
                        )}
                        {availableFormats.includes("csv") && (
                          <SelectItem value="csv" className={styles.selectItem}>
                            <CiFileOn size={18} />
                            CSV
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </section>
                </div>
              )}
            </div>
            
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              size="default"
              onClick={handleCancel}
              disabled={disabled}
            >
              {cancelButtonText}
            </Button>
            <Button
              variant="primary"
              size="default"
              loading={isSharing}
              onClick={handleShare}
              disabled={!isFormValid() || disabled}
            >
              {shareButtonText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add External Staff Modal */}
      <AddExternalStaff
        isOpen={showAddExternalStaffModal}
        onClose={handleCloseAddExternalStaff}
        comp_renderer={() => { }} // Not needed for this use case
        current_render={false} // Not needed for this use case
        onSuccess={handleAddExternalStaffSuccess}
      />
    </>
  );
}
